import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import JobStatus from './JobStatus';
import { PlusOutlined } from '@ant-design/icons';
import Button from 'components/new-ui/base/button/Button';
import { Space, TabsProps } from 'antd';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import useJobs from 'hooks/useJobs';
import useSession from 'hooks/useSession';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { JobStatuses, UpdateExternalReferencesPayload } from 'store/jobs/job';
import moment from 'moment';
import NewDeclarationDrawer, { NewDeclarationDrawerFormValues } from 'views/declarations/common/NewDeclarationDrawer';
import Notification from 'components/ui/base/notification/Notification';
import { BrokerUploadLocationState } from 'views/invoice-upload/broker-upload/types';
import { hasPermissions } from 'config/roles/hasPermissions';
import { JobTabKey, JobTabKeys } from './types';
import { StyledTabs } from 'components/new-ui/base/tabs/styles';
import { getJobDetailsActiveTabKey } from './utils';
import { StyledBadge } from 'components/new-ui/base/status-indicator/styles';
import ExternalReferencesModal from 'views/manifests/components/ExternalReferencesModal';
import { getErrorMessage } from 'utils/getErrorMessage';
import { ExternalReference, getExternalReferencesFromJob, ReferenceTypes } from 'utils/external-references';
import { Team } from 'store/teams/types';
import { getTeamById } from 'store/teams/client';
import EditJobModal from './components/edit-job/EditJobModal';

type Props = {
    children: ReactNode;
};

type TabItem = Exclude<TabsProps['items'], undefined>[number];

const JobDetailsPage = ({ children }: Props) => {
    const location = useLocation();
    const { jobId } = useParams<{ jobId: string }>();
    const {
        job,
        updateExternalReferences,
        isLoading: isLoadingJob,
        error,
        invoicesCount,
        getJobInvoicesCount,
    } = useJobs({ jobId });
    const { userInfo } = useSession();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const navigate = useNavigate();
    const { setBreadcrumbRoutes } = useBreadcrumb();

    const [showNewDeclarationDrawer, setShowNewDeclarationDrawer] = useState(false);
    const [showExternalReferencesModal, setShowExternalReferencesModal] = useState(false);
    const [showEditJobModal, setShowEditJobModal] = useState(false);
    const [team, setTeam] = useState<Team>();

    const activeTabKey = getJobDetailsActiveTabKey(location);
    const canCreateDeclarations = hasPermissions(userInfo, 'declarations', 'CREATE');
    const canUpdateJobs = hasPermissions(userInfo, 'jobs', 'UPDATE');
    const hasJobExternalRefernencesPermissions = hasPermissions(userInfo, 'jobs', 'UPDATE_EXTERNAL_REFERENCES');
    const isCompleteJob = [JobStatuses.COMPLETED, JobStatuses.REJECTED, JobStatuses.CLOSED].includes(job?.status);
    const isDraftJob = job?.status === JobStatuses.NEW;

    const shouldHideCreateDeclaration = !canCreateDeclarations || isCompleteJob;

    const handleNavigateBack = () => {
        navigate(-1);
    };

    const handleNavigateWithJobState = (values: NewDeclarationDrawerFormValues) => {
        const to = `/invoice-upload/${values.country}/${values.declarationType}`;
        const locationState: BrokerUploadLocationState = { job, jobLocked: true };
        navigate(to, { state: locationState });
    };

    const handleUpdateExternalReferences = async (reference: ExternalReference) => {
        try {
            if (!job.id) {
                throw new Error('Could not get job details. Please refresh the page and try again.');
            }

            const payload: UpdateExternalReferencesPayload = {
                ens: job.externalReferences?.ens,
                pbn: job.externalReferences?.pbn,
                gmr: job.externalReferences?.gmr,
                cdsExportMRN: job.externalReferences?.cdsExportMRN,
            };

            if (reference.referenceType === ReferenceTypes.ENS) {
                payload.ens = reference.referenceNumber;
            } else if (reference.referenceType === ReferenceTypes.PBN) {
                payload.pbn = reference.referenceNumber;
            } else if (reference.referenceType === ReferenceTypes.GMR) {
                payload.gmr = reference.referenceNumber;
            } else if (reference.referenceType === ReferenceTypes.UK_CDS_MRN) {
                payload.cdsExportMRN = reference.referenceNumber;
            }

            await updateExternalReferences(job.id, payload);

            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'External Reference Updated',
                description: 'External references have been successfully updated.',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: errorMessage || 'Could not update external references for this job.',
            });
        }
    };

    const handleRemoveExternalReference = async (reference: ExternalReference) => {
        try {
            if (!job.id) {
                throw new Error('Could not get job details. Please refresh the page and try again.');
            }

            const payload: UpdateExternalReferencesPayload = {
                ens: job.externalReferences?.ens,
                pbn: job.externalReferences?.pbn,
                gmr: job.externalReferences?.gmr,
                cdsExportMRN: job.externalReferences?.cdsExportMRN,
                [reference.referenceType]: null,
            };

            await updateExternalReferences(job.id, payload);
            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'External Reference Added',
                description: 'External reference has been successfully removed.',
            });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'External Reference Error',
                description: errorMessage || 'Could not update external references for this job.',
            });
        }
    };

    useEffect(() => {
        getJobInvoicesCount(job?.id);
    }, [getJobInvoicesCount, job?.id]);

    useEffect(() => {
        if (job?.teamId) {
            getTeamById(job.teamId).then((res) => setTeam(res));
        }
    }, [job?.teamId]);

    const tabsItems: TabItem[] = useMemo(
        () => [
            { key: JobTabKeys.OVERVIEW, label: 'Overview' },
            { key: JobTabKeys.DOCUMENTS, label: 'Documents' },
            { key: JobTabKeys.NOTES, label: 'Notes' },
            {
                key: JobTabKeys.INVOICES,
                label: <>Invoices {invoicesCount > 0 && <StyledBadge color="var(--gray-6)" count={invoicesCount} />}</>,
            },
            { key: JobTabKeys.HISTORY, label: 'History' },
        ],
        [invoicesCount]
    );

    useEffect(() => {
        if (isLoadingJob) {
            showGlobalOverlay({ type: 'LoadingOverlay' });
        } else {
            hideGlobalOverlay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingJob]);

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Home',
                path: '/home',
            },
            {
                breadcrumbName: 'Jobs',
                path: '../jobs',
            },
            {
                breadcrumbName: job?.referenceId ?? '-',
                path: '',
            },
        ]);
    }, [job?.referenceId, setBreadcrumbRoutes]);

    if (error) {
        return <Navigate to="/jobs" />;
    }

    const TIME_FORMAT = 'DD/MM/YY';
    const formattedCreatedAt = moment(job?.createdAt).format(TIME_FORMAT);

    return (
        <>
            <StyledPageWrapper>
                <PageHeader
                    title="Job Details"
                    icon={null}
                    showBackButton
                    onBackButtonClick={handleNavigateBack}
                    additionalContent={
                        <div style={{ display: 'flex', gap: 16, marginLeft: 16, alignItems: 'center' }}>
                            <span>Job ID: {job?.referenceId}</span>
                            <JobStatus status={job?.status} />
                            <span>
                                Created By: {job?.creator?.fullName ?? '-'}, {formattedCreatedAt}
                            </span>
                            {team && <span>Team: {team.name}</span>}
                        </div>
                    }
                    sidebar={
                        <Space size={8}>
                            {canUpdateJobs && isDraftJob && (
                                <Button onClick={() => setShowEditJobModal(true)}>Edit Job</Button>
                            )}
                            {!shouldHideCreateDeclaration && (
                                <Button
                                    icon={<PlusOutlined />}
                                    type="primary"
                                    onClick={() => setShowNewDeclarationDrawer(true)}
                                >
                                    New Declaration
                                </Button>
                            )}
                        </Space>
                    }
                />

                <StyledTabs
                    style={{
                        marginTop: 24,
                    }}
                    items={tabsItems}
                    activeKey={activeTabKey}
                    onChange={(k) => {
                        const activeKey = k as JobTabKey;
                        const to = activeKey === JobTabKeys.OVERVIEW ? `/jobs/${jobId}` : activeKey;
                        navigate(to, {
                            replace: true,
                        });
                    }}
                    tabBarExtraContent={
                        <>
                            {hasJobExternalRefernencesPermissions && (
                                <Button onClick={() => setShowExternalReferencesModal(true)}>
                                    Add External Reference
                                </Button>
                            )}
                        </>
                    }
                />

                {children}
            </StyledPageWrapper>

            <NewDeclarationDrawer
                open={showNewDeclarationDrawer}
                onClose={() => setShowNewDeclarationDrawer(false)}
                onSelectDeclarationInfo={handleNavigateWithJobState}
            />

            {job && (
                <ExternalReferencesModal
                    type="job"
                    open={showExternalReferencesModal}
                    onClose={() => setShowExternalReferencesModal(false)}
                    externalReferences={getExternalReferencesFromJob(job)}
                    onUpdateExternalReferences={handleUpdateExternalReferences}
                    onRemoveExternalReference={handleRemoveExternalReference}
                />
            )}

            {job && <EditJobModal open={showEditJobModal} job={job} onClose={() => setShowEditJobModal(false)} />}
        </>
    );
};

export default JobDetailsPage;
